/**
 * Unified Session Viewer
 *
 * Route-level orchestrator that composes TranscriptPanel + VideoPanel + ViewerToolbar.
 * Fetches session metadata to auto-show only active sources:
 * - Both transcript + video → split screen
 * - Only transcript → transcript fullscreen
 * - Only video → video fullscreen
 */

import { isNativeWebView as isNativeWebViewShared } from '@polygraf/shared'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { FileDownloadBar } from '../components/viewer/file-download-bar'
import { IphoneFullscreenHint } from '../components/viewer/iphone-fullscreen-hint'
import { TranscriptPanel } from '../components/viewer/transcript-panel'
import { VideoPanel } from '../components/viewer/video-panel'
import type { LayoutMode } from '../components/viewer/viewer-toolbar'
import { ShortcutsLegend, ViewerToolbar } from '../components/viewer/viewer-toolbar'
import { useImmersiveDoubleTap } from '../hooks/use-immersive-double-tap'
import { usePageTitle } from '../hooks/use-page-title'
import { useSessionFile } from '../hooks/use-session-file'
import { exitBrowserFullscreen } from '../lib/exit-browser-fullscreen'
import { trpc } from '../trpc/client'

/** True when running inside the React Native WebView (mobile app has its own header) */
const isNativeWebView = isNativeWebViewShared()

interface LayoutState {
	layout: LayoutMode
	transcriptVisible: boolean
	videoVisible: boolean
}

const LAYOUT_KEY = 'polygraf-unified-viewer-layout'

function loadLayout(): LayoutState | null {
	try {
		const saved = localStorage.getItem(LAYOUT_KEY)
		if (saved) {
			return JSON.parse(saved)
		}
	} catch {
		// Ignore errors
	}
	return null
}

function saveLayout(state: LayoutState): void {
	try {
		localStorage.setItem(LAYOUT_KEY, JSON.stringify(state))
	} catch {
		// Ignore errors
	}
}

export default function UnifiedViewerPage() {
	const { t } = useTranslation()
	const { sessionId } = useParams<{ sessionId: string }>()
	const containerRef = useRef<HTMLElement>(null)

	// Fetch session metadata to know which sources are active.
	// On NOT_FOUND (session ended or never existed) we render an
	// explicit "session ended" state instead of letting the panels
	// spin on "waiting for writer" forever (MP-156).
	const { data: session, error: sessionError } = trpc.liveSessions.getByDocumentId.useQuery(
		{ documentId: sessionId ?? '' },
		{
			enabled: !!sessionId,
			// Stop polling once the session is gone — it won't come back.
			refetchInterval: (query) => {
				const code = query.state.error?.data?.code
				if (code === 'NOT_FOUND' || code === 'BAD_REQUEST') return false
				return 15_000
			},
			retry: (failureCount, err) => {
				const code = err?.data?.code
				if (code === 'NOT_FOUND' || code === 'BAD_REQUEST') return false
				return failureCount < 3
			},
		},
	)

	// Screen readers announce the title first. Left as the bare app name, a
	// live session was "Polygraf Online, document" and then silence (retest
	// 24. 9. 2026, VoiceOver and NVDA).
	const pageTitle = session?.title || t('viewer.transcript.title')
	usePageTitle(pageTitle)

	const sessionGone =
		sessionError?.data?.code === 'NOT_FOUND' || sessionError?.data?.code === 'BAD_REQUEST'

	const [layoutState, setLayoutState] = useState<LayoutState>(() => {
		const saved = loadLayout()
		const isMobile = window.innerWidth < 1024
		return {
			layout: isMobile ? 'stacked' : (saved?.layout ?? 'side-by-side'),
			// Don't restore saved visibility — let auto-configure set it from session metadata
			transcriptVisible: true,
			videoVisible: true,
		}
	})
	const [isFullscreen, setIsFullscreen] = useState(false)
	// Immersive: every control hidden, the panels take the whole screen. This
	// is the mode MU tests on phones ("tap the middle, everything disappears").
	// The browser Fullscreen API is only an extra on top: iPhone Safari does not
	// implement it for pages at all, so a mode that depended on it did nothing
	// there (Natálie's retest, 24. 9. 2026, items A1/A4).
	const [immersive, setImmersive] = useState(false)
	const [showShortcuts, setShowShortcuts] = useState(false)
	const initializedRef = useRef(false)

	// Auto-configure panel visibility based on session metadata (once)
	useEffect(() => {
		if (!session || initializedRef.current) return
		initializedRef.current = true

		const isMobile = window.innerWidth < 1024
		const hasBoth = session.hasTranscript && session.hasVideo

		setLayoutState((prev) => ({
			...prev,
			layout: isMobile || !hasBoth ? 'stacked' : prev.layout,
			transcriptVisible: session.hasTranscript,
			videoVisible: session.hasVideo,
		}))
	}, [session])

	// Connection status reported by child panels via callbacks
	const [transcriptConnected, setTranscriptConnected] = useState(false)
	const [videoConnected, setVideoConnected] = useState(false)

	// Persist layout changes
	useEffect(() => {
		if (layoutState) {
			saveLayout(layoutState)
		}
	}, [layoutState])

	// The fullscreen button and the F key toggle immersive mode; the Fullscreen
	// API is attempted on top where it exists. It needs a user gesture and a
	// permissive iframe policy, so a rejection must not undo the mode the user
	// just asked for. Same contract as transcript-viewer.tsx.
	const toggleFullscreen = useCallback(async () => {
		setImmersive((prev) => !prev)
		if (isNativeWebView || !containerRef.current) return
		try {
			if (!document.fullscreenElement) {
				await containerRef.current.requestFullscreen?.()
			} else {
				await document.exitFullscreen()
			}
		} catch (err) {
			console.warn('[UnifiedViewer] fullscreen request refused:', err)
		}
	}, [])

	useEffect(() => {
		const handleFullscreenChange = () => {
			const active = !!document.fullscreenElement
			setIsFullscreen(active)
			// Leaving fullscreen through the browser's own Escape must also bring
			// the controls back, or the button reads "exit" with nothing to exit.
			if (!active) setImmersive(false)
		}
		document.addEventListener('fullscreenchange', handleFullscreenChange)
		return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
	}, [])

	/**
	 * Double tap on a panel leaves immersive mode; it never enters it — the
	 * toolbar button and F do (MU 25. 9. 2026). Taps on controls, the tap that
	 * ends a text selection, a scroll and a pinch never count. See
	 * decideImmersiveTap.
	 */
	const exitImmersiveFromTap = useCallback(() => {
		setImmersive(false)
		exitBrowserFullscreen()
	}, [])
	const panelTap = useImmersiveDoubleTap(immersive, exitImmersiveFromTap)

	// The mobile app draws its own native header above this WebView. Tell it to
	// hide that header too, or "everything disappears" leaves one bar behind.
	useEffect(() => {
		if (!isNativeWebView) return
		try {
			const bridge = (
				window as unknown as { ReactNativeWebView?: { postMessage: (s: string) => void } }
			).ReactNativeWebView
			bridge?.postMessage(
				JSON.stringify({ source: 'unified-viewer', type: 'immersive', immersive }),
			)
		} catch (err) {
			console.warn('[UnifiedViewer] immersive bridge failed:', err)
		}
	}, [immersive])

	// Keyboard shortcuts
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return

			switch (e.key.toLowerCase()) {
				case 'f':
					if (!e.ctrlKey && !e.metaKey) {
						toggleFullscreen()
					}
					break
				case 'l':
					setLayoutState((prev) => ({
						...prev,
						layout: prev.layout === 'stacked' ? 'side-by-side' : 'stacked',
					}))
					break
				case '1':
					setLayoutState((prev) => ({ ...prev, transcriptVisible: !prev.transcriptVisible }))
					break
				case '2':
					setLayoutState((prev) => ({ ...prev, videoVisible: !prev.videoVisible }))
					break
				case 'escape':
					if (showShortcuts) {
						setShowShortcuts(false)
					} else if (immersive) {
						setImmersive(false)
					}
					break
				case '?':
					setShowShortcuts((prev) => !prev)
					break
			}
		}

		window.addEventListener('keydown', handleKeyDown)
		return () => window.removeEventListener('keydown', handleKeyDown)
	}, [toggleFullscreen, showShortcuts, immersive])

	const {
		file: sessionFile,
		downloadUrl: fileDownloadUrl,
		setFile: setSessionFile,
	} = useSessionFile({
		documentId: sessionId ?? '',
	})

	const handleFileUploaded = useCallback(
		(file: import('@polygraf/shared').SessionFileInfo) => {
			setSessionFile(file)
		},
		[setSessionFile],
	)

	const handleFileRemoved = useCallback(() => {
		setSessionFile(null)
	}, [setSessionFile])

	if (!sessionId) {
		return (
			<div className="flex items-center justify-center h-screen bg-base-200">
				<p className="text-base-content/60">{t('viewer.noSession')}</p>
			</div>
		)
	}

	if (sessionGone) {
		return (
			<div className="flex items-center justify-center h-screen bg-base-200 p-6">
				<div className="text-center max-w-md">
					<div className="w-16 h-16 mx-auto rounded-full bg-amber-500/10 flex items-center justify-center mb-4">
						<svg
							className="w-8 h-8 text-amber-500"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
							aria-hidden="true"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
							/>
						</svg>
					</div>
					<h1 className="text-2xl font-semibold mb-2">{t('sessions.join.errors.notFoundTitle')}</h1>
					<p className="text-base-content/60">{t('sessions.join.errors.notFound')}</p>
				</div>
			</div>
		)
	}

	const { layout, transcriptVisible, videoVisible } = layoutState
	const isSideBySide = layout === 'side-by-side'

	// Don't mount VideoPanel until the session metadata says whether this
	// session has video. `videoVisible` defaults to true, so mounting on that
	// default fetched a LiveKit token and opened the signal WebSocket before the
	// metadata arrived; on a transcript-only session the auto-configure effect
	// then hid the panel a few ms later and the join was aborted mid-handshake
	// (LiveKit logs "could not handle new participant: request canceled" at
	// ERROR and creates and closes an empty room).
	//
	// On the render where the metadata first arrives the auto-configure effect
	// has not run yet (a child's effects run before the parent's), so take
	// visibility from the metadata directly on that pass; otherwise the panel
	// would still mount once and request a token it never uses. Once the
	// metadata fetch has failed (after react-query's retries), fall back to the
	// stored visibility, which defaults to showing the panel.
	const showVideo =
		session !== undefined
			? initializedRef.current
				? videoVisible
				: session.hasVideo
			: sessionError != null && videoVisible

	return (
		<main ref={containerRef} className="flex flex-col h-screen-dynamic bg-base-200 relative">
			{/* The page's one heading, for screen-reader navigation (H key / rotor). */}
			<h1 className="sr-only">{pageTitle}</h1>
			<IphoneFullscreenHint active={immersive} />
			{!isNativeWebView && !immersive && (
				<ViewerToolbar
					sessionId={sessionId}
					room={session?.room ?? null}
					breadcrumbLinked={!isNativeWebView}
					layout={layout}
					onLayoutChange={(l) => setLayoutState((prev) => ({ ...prev, layout: l }))}
					transcriptVisible={transcriptVisible}
					onTranscriptVisibleChange={(v) =>
						setLayoutState((prev) => ({ ...prev, transcriptVisible: v }))
					}
					videoVisible={videoVisible}
					onVideoVisibleChange={(v) => setLayoutState((prev) => ({ ...prev, videoVisible: v }))}
					transcriptConnected={transcriptConnected}
					videoConnected={videoConnected}
					isFullscreen={isFullscreen || immersive}
					onToggleFullscreen={toggleFullscreen}
				/>
			)}

			{showShortcuts && <ShortcutsLegend onClose={() => setShowShortcuts(false)} />}

			{/* Panel container. A double tap here leaves immersive mode (it never enters). */}
			{/* touch-manipulation: iOS would otherwise spend the double tap on its own zoom.
			    Trade-off: that also turns off iOS double-tap-to-zoom here, which some
			    low-vision readers use. Pinch zoom still works (manipulation keeps it),
			    and the transcript's font-size setting remains. */}
			{/* biome-ignore lint/a11y/useKeyWithClickEvents lint/a11y/noStaticElementInteractions: the panel area must not
			    become a focus stop; keyboard users have the toolbar button, F and Escape. */}
			<div
				className={`flex-1 flex min-h-0 touch-manipulation ${isSideBySide ? 'flex-row' : 'flex-col'}`}
				onPointerDown={panelTap.onPointerDown}
				onClick={panelTap.onClick}
			>
				{showVideo && (
					<div
						className={`flex flex-col min-w-0 min-h-0 ${
							transcriptVisible ? 'flex-1 basis-0' : 'flex-1'
						}`}
					>
						<VideoPanel
							sessionId={sessionId}
							immersive={immersive}
							onConnectionChange={setVideoConnected}
							className="flex-1 min-h-0"
						/>
						{sessionFile && <FileDownloadBar file={sessionFile} downloadUrl={fileDownloadUrl} />}
					</div>
				)}
				{transcriptVisible && (
					<TranscriptPanel
						sessionId={sessionId}
						immersive={immersive}
						hideRefresh={isNativeWebView}
						onConnectionChange={setTranscriptConnected}
						onFileUploaded={handleFileUploaded}
						onFileRemoved={handleFileRemoved}
						className={`min-w-0 min-h-0 ${showVideo ? 'flex-1 basis-0' : 'flex-1'}`}
					/>
				)}
			</div>
		</main>
	)
}
